import type {
  CommonResponse,
  CommonResponseInteger,
  CommonResponseListProblemResponse,
  CommonResponseWorksheetCreationFilterResponse,
  CommonResponseWorksheetDetailResponse,
  CommonResponseWorksheetFilterCreationResponse,
  CreateGroupWorksheetFilterByWeakWorksheetRequest,
  CreateWorksheetFilterByConceptRequest,
  CreateWorksheetFilterByCustomPaperRequest,
  CreateWorksheetFilterByDateRangeRequest,
  CreateWorksheetFilterByMergingWorksheetRequest,
  CreateWorksheetFilterByOneClickRequest,
  CreateWorksheetFilterByPracticeRequest,
  CreateWorksheetFilterByPublicWorkbookRequest,
  CreateWorksheetFilterBySchoolTestPaperOriginalRequest,
  CreateWorksheetFilterBySchoolTestPaperRequest,
  CreateWorksheetFilterBySchoolTestRequest,
  CreateWorksheetFilterBySchoolTestSimilarRequest,
  CreateWorksheetFilterByWeakConceptRequest,
  CreateWorksheetFilterByWeakWorksheetRequest,
  CustomWorksheetCreationRequest,
  CustomWorksheetSearchRequest,
  CustomWorksheetUpdateRequest,
  GetWorksheetAndWorksheetPdfAndWorksheetFilterByWorksheetIdParams,
  MyDbProblemSearchRequest,
  ProblemResponse,
  UpdateWorksheetRequest,
  WorksheetFilterCreationResponse,
} from '~/api-spec/__generated__/main'
import { httpClient, type RequestConfig } from '~/http/client'
import type { WithDefaultedOptionals, WithNullableOptionals } from '~/http/request'

import {
  adaptCreatedWorksheetId,
  adaptMakeWorksheetProblems,
  adaptRecommendedProblemCount,
  adaptWorksheetDetail,
  adaptWorksheetFilterDraft,
  adaptWorksheetFilterId,
} from './adapters'
import type {
  MakeWorksheetProblem,
  WorksheetDetail,
  WorksheetFilterDraft,
  WorksheetFilterId,
} from './schemas'

// 조건을 바꿀 때마다 필터를 다시 만들고 다시 추천하므로 앞선 요청을 버릴 수 있게 signal 을 받는다
type _Config = Pick<RequestConfig, 'meta' | 'signal'>

type _SelectedChapters = 'selectedConceptIdList' | 'selectedLittleChapterIdList'

// 2026-10-08 백엔드 origin/develop(08ff3525b) 의 worksheet/controller/request/*Request.kt 로 확인한 요청 스펙과 실제의 차이:
//   - Kotlin nullable 필드를 스펙이 optional 로만 그린다. WithNullableOptionals 로 null 을 되살린다
//   - 기본값이 있는 필드(WithDefaultedOptionals 의 두 번째 인자)는 생략할 수 있다
//   - 개념·취약유형·기간·학습지별 필터 요청의 rateRange 는 Kotlin 계산 프로퍼티(minRate..maxRate)가 스펙에 새어
//     나온 것이라 보내지 않는다
//   - 그룹 학습지 요청의 token·worksheetIdToStudent·practiceTestSearchType 은 생성자 밖의 서버 내부 프로퍼티다
export type ConceptWorksheetFilterBody = WithDefaultedOptionals<
  WithNullableOptionals<Omit<CreateWorksheetFilterByConceptRequest, 'rateRange'>>,
  | _SelectedChapters
  | 'arithmeticOnly'
  | 'excludePrevious'
  | 'includeMyDbProblem'
  | 'excludedTopicIds'
  | 'excludedSubTopicIds'
>
export type PublicWorkbookWorksheetFilterBody = WithDefaultedOptionals<
  WithNullableOptionals<CreateWorksheetFilterByPublicWorkbookRequest>,
  _SelectedChapters | 'exampleProblemIds' | 'excludePrevious' | 'includeSameProblem'
>
export type PracticeTestWorksheetFilterBody = WithDefaultedOptionals<
  WithNullableOptionals<CreateWorksheetFilterByPracticeRequest>,
  _SelectedChapters
>
export type SchoolTestWorksheetFilterBody = WithDefaultedOptionals<
  WithNullableOptionals<CreateWorksheetFilterBySchoolTestRequest>,
  _SelectedChapters | 'excludePrevious'
>
export type CustomPaperWorksheetFilterBody = WithDefaultedOptionals<
  WithNullableOptionals<CreateWorksheetFilterByCustomPaperRequest>,
  _SelectedChapters
>
export type SchoolTestPaperWorksheetFilterBody = WithDefaultedOptionals<
  WithNullableOptionals<CreateWorksheetFilterBySchoolTestPaperRequest>,
  _SelectedChapters
>
export type WeakConceptWorksheetFilterBody = WithDefaultedOptionals<
  WithNullableOptionals<Omit<CreateWorksheetFilterByWeakConceptRequest, 'rateRange'>>,
  _SelectedChapters
>
export type WeakPeriodWorksheetFilterBody = WithDefaultedOptionals<
  WithNullableOptionals<Omit<CreateWorksheetFilterByDateRangeRequest, 'rateRange'>>,
  _SelectedChapters
>
export type WeakWorksheetWorksheetFilterBody = WithDefaultedOptionals<
  WithNullableOptionals<Omit<CreateWorksheetFilterByWeakWorksheetRequest, 'rateRange'>>,
  _SelectedChapters | 'arithmeticOnly'
>
export type OneClickWorksheetFilterBody = WithDefaultedOptionals<
  WithNullableOptionals<CreateWorksheetFilterByOneClickRequest>,
  _SelectedChapters
>
export type MergedWorksheetFilterBody =
  WithNullableOptionals<CreateWorksheetFilterByMergingWorksheetRequest>
export type MyDbOriginalWorksheetFilterBody =
  WithNullableOptionals<CreateWorksheetFilterBySchoolTestPaperOriginalRequest>
export type MyDbSimilarWorksheetFilterBody = WithDefaultedOptionals<
  WithNullableOptionals<CreateWorksheetFilterBySchoolTestSimilarRequest>,
  'similarX' | 'similarLevel' | 'onlyAutoScorable'
>
export type RecommendedProblemSearchBody = WithNullableOptionals<CustomWorksheetSearchRequest>
export type MyDbProblemSearchBody = MyDbProblemSearchRequest
export type WorksheetCreationBody = WithDefaultedOptionals<
  WithNullableOptionals<CustomWorksheetCreationRequest>,
  'shareScope' | 'qrFlag' | 'problemTrendFlag' | 'problemPadding' | 'problemList' | 'prefix'
>
export type WorksheetUpdateBody = WithDefaultedOptionals<
  WithNullableOptionals<CustomWorksheetUpdateRequest>,
  'qrFlag' | 'problemTrendFlag' | 'problemPadding' | 'problemList' | 'prefix'
>
export type WorksheetPartUpdateBody = WithDefaultedOptionals<
  WithNullableOptionals<UpdateWorksheetRequest>,
  'qrFlag' | 'problemTrendFlag' | 'problemPadding' | 'prefix'
>
export type GroupWorksheetCreationBody = WithDefaultedOptionals<
  WithNullableOptionals<
    Omit<
      CreateGroupWorksheetFilterByWeakWorksheetRequest,
      'token' | 'worksheetIdToStudent' | 'practiceTestSearchType'
    >
  >,
  | _SelectedChapters
  | 'excludeOOC'
  | 'onlyAutoScorable'
  | 'excludePrevious'
  | 'maxProblemCount'
  | 'problemCount'
  | 'arithmeticOnly'
  | 'qrFlag'
  | 'shareScope'
  | 'problemPadding'
>
export type WorksheetDetailParams = Omit<
  GetWorksheetAndWorksheetPdfAndWorksheetFilterByWorksheetIdParams,
  'id'
>

/***************** 1단계: 조건을 맡기고 filterId 받기 *****************/
// filterId 를 실어 보내면 그 필터를 고친다. 기출 업로드 v2 두 종류와 학습지 합치기는 봉투 없이 본문을 바로 낸다.

const _createFilter =
  <Body>(url: string) =>
  async (body: Body, config?: _Config): Promise<WorksheetFilterId> => {
    const res = await httpClient().post<CommonResponseWorksheetFilterCreationResponse>(
      url,
      body,
      config,
    )
    return adaptWorksheetFilterId(res.data)
  }

const _createRawFilter =
  <Body>(url: string) =>
  async (body: Body, config?: _Config): Promise<WorksheetFilterId> =>
    adaptWorksheetFilterId(
      await httpClient().post<WorksheetFilterCreationResponse>(url, body, config),
    )

/** 단원·유형별(연산 포함, arithmeticOnly 로 가른다) */
export const createConceptWorksheetFilter = _createFilter<ConceptWorksheetFilterBody>(
  '/worksheet/filter/concept',
)
export const createPublicWorkbookWorksheetFilter = _createFilter<PublicWorkbookWorksheetFilterBody>(
  '/worksheet/filter/public-workbook',
)
export const createPracticeTestWorksheetFilter = _createFilter<PracticeTestWorksheetFilterBody>(
  '/worksheet/filter/practice',
)
export const createSchoolTestWorksheetFilter = _createFilter<SchoolTestWorksheetFilterBody>(
  '/worksheet/filter/school-test',
)
export const createCustomPaperWorksheetFilter = _createFilter<CustomPaperWorksheetFilterBody>(
  '/worksheet/filter/custom-paper',
)
export const createSchoolTestPaperWorksheetFilter =
  _createFilter<SchoolTestPaperWorksheetFilterBody>('/worksheet/filter/school-test-paper')
/** 오답 학습지 — 단원별 */
export const createWeakConceptWorksheetFilter = _createFilter<WeakConceptWorksheetFilterBody>(
  '/worksheet/filter/weak-concept',
)
/** 오답 학습지 — 기간별 */
export const createWeakPeriodWorksheetFilter = _createFilter<WeakPeriodWorksheetFilterBody>(
  '/worksheet/filter/weak-period',
)
/** 오답 학습지 — 학습지별 */
export const createWeakWorksheetWorksheetFilter = _createFilter<WeakWorksheetWorksheetFilterBody>(
  '/worksheet/filter/weak-worksheet',
)
export const createOneClickWorksheetFilter = _createFilter<OneClickWorksheetFilterBody>(
  '/worksheet/filter/one-click',
)
/** 학습지 합치기 */
export const createMergedWorksheetFilter = _createRawFilter<MergedWorksheetFilterBody>(
  '/worksheet/filter/merged-worksheet',
)
/** 기출 업로드 — 원본 학습지 */
export const createMyDbOriginalWorksheetFilter = _createRawFilter<MyDbOriginalWorksheetFilterBody>(
  '/v2/worksheet/filter/school-test-paper/original',
)
/** 기출 업로드 — 유사 학습지 */
export const createMyDbSimilarWorksheetFilter = _createRawFilter<MyDbSimilarWorksheetFilterBody>(
  '/v2/worksheet/filter/school-test-paper/similar',
)

/***************** 2단계: 조건으로 문제 고르기 *****************/

export const getWorksheetFilterDraft = async (
  filterId: WorksheetFilterId,
  config?: _Config,
): Promise<WorksheetFilterDraft> => {
  const body = await httpClient().get<CommonResponseWorksheetCreationFilterResponse>(
    '/worksheet/filter',
    { params: { filterId }, ...config },
  )
  return adaptWorksheetFilterDraft(body.data)
}

/** 조건에 맞는 문제가 몇 개인지 */
export const getRecommendedProblemCount = async (
  filterId: WorksheetFilterId,
  config?: _Config,
): Promise<number> => {
  const body = await httpClient().get<CommonResponseInteger>('/worksheet/problem/count', {
    params: { filterId },
    ...config,
  })
  return adaptRecommendedProblemCount(body.data)
}

/** 조건에 맞는 문제를 추천받는다. 범위·제외할 문제를 바꿔 다시 부르면 새 문제를 받는다 */
export const searchRecommendedProblems = async (
  body: RecommendedProblemSearchBody,
  config?: _Config,
): Promise<MakeWorksheetProblem[]> => {
  const res = await httpClient().post<CommonResponseListProblemResponse>(
    '/worksheet/problem',
    body,
    config,
  )
  return adaptMakeWorksheetProblems(res.data) ?? []
}

/** 고른 유형 범위의 나의DB 문제 전체. 이 엔드포인트는 봉투 없이 본문을 바로 낸다 */
export const getMyDbProblems = async (
  body: MyDbProblemSearchBody,
  config?: _Config,
): Promise<MakeWorksheetProblem[]> =>
  adaptMakeWorksheetProblems(
    await httpClient().post<ProblemResponse[]>('/worksheet/filter/my-db/problem', body, config),
  ) ?? []

/***************** 3단계: 학습지 만들기·고치기 *****************/

export const createWorksheet = async (
  body: WorksheetCreationBody,
  config?: _Config,
): Promise<WorksheetDetail['id']> => {
  const res = await httpClient().post<CommonResponseInteger>('/worksheet', body, config)
  return adaptCreatedWorksheetId(res.data)
}

/** 출제되지 않은 학습지를 문제까지 고친다 */
export const updateWorksheet = async (
  worksheetId: WorksheetDetail['id'],
  body: WorksheetUpdateBody,
  config?: _Config,
): Promise<void> => {
  await httpClient().put<CommonResponse>(`/worksheet/${worksheetId}`, body, config)
}

/** 출제된 학습지는 문제를 두고 디자인·메타만 고친다 */
export const updateWorksheetPart = async (
  worksheetId: WorksheetDetail['id'],
  body: WorksheetPartUpdateBody,
  config?: _Config,
): Promise<void> => {
  await httpClient().patch<CommonResponse>(`/worksheet/${worksheetId}`, body, config)
}

/** 학생별 오답학습지. 학습지는 서버가 학생마다 비동기로 만들어 응답에 id 가 없다 */
export const createGroupWorksheets = async (
  body: GroupWorksheetCreationBody,
  config?: _Config,
): Promise<void> => {
  await httpClient().post<CommonResponse>('/worksheet/group', body, config)
}

/** 학습지 하나의 메타·PDF·문제·필터 */
export const getWorksheetDetail = async (
  worksheetId: WorksheetDetail['id'],
  params?: WorksheetDetailParams,
  config?: _Config,
): Promise<WorksheetDetail> => {
  const body = await httpClient().get<CommonResponseWorksheetDetailResponse>(
    `/worksheet/${worksheetId}`,
    { params, ...config },
  )
  return adaptWorksheetDetail(body.data)
}
